import { Button, ButtonProps, LinkProps } from '@chakra-ui/react'
import NextLink from 'next/link'
import { forwardRef } from 'react'

export type ButtonLinkProps = ButtonProps & LinkProps

/** Next-only link props a caller may pass through (not in the Chakra types). */
type NextOnlyLinkProps = { prefetch?: boolean | null; replace?: boolean; scroll?: boolean; shallow?: boolean }

/**
 * A Button rendered as a Next link. Only link props go to `NextLink`; everything else (style props,
 * aria, handlers) stays on the Button, which merges onto the anchor through `asChild`. Spreading every
 * prop onto `NextLink` leaked Chakra style props (`justifyContent`, `paddingX`, `bgColor`, …) to the
 * DOM and React warned on every Advisors page (#34).
 */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>((props, ref) => {
  const { children, href, prefetch, rel, replace, scroll, shallow, target, ...buttonProps } =
    props as ButtonLinkProps & NextOnlyLinkProps

  return (
    <Button asChild={true} {...buttonProps}>
      <NextLink
        ref={ref}
        href={href ?? ''}
        prefetch={prefetch}
        rel={rel}
        replace={replace}
        scroll={scroll}
        shallow={shallow}
        target={target}
      >
        {children}
      </NextLink>
    </Button>
  )
})

ButtonLink.displayName = 'ButtonLink'
